import { t } from 'i18next';
import { isObject } from 'lodash';

import { ImportHeader, ImportType, ManufacturerValidationStatus } from 'bom/enums';
import {
  IImportNotValidPartsFilters,
  IImportPart,
  IImportUpsertPartData,
  IImportUpsertPartsData,
  IManufacturerOption,
  ISaveUomsData,
  IUomTableRow,
} from 'bom/types';
import { IOption } from 'core/types';
import { PricingHelper } from 'pricing/helpers';

const mapUpsertImportPartData = (
  id: number,
  field: string,
  newValue?: number | { id: number } | boolean
) => {
  let mapData: IImportUpsertPartData = id
    ? {
        id,
      }
    : {};

  switch (field) {
    case 'manufacturer':
      mapData = {
        ...mapData,
        manufacturerId: !isObject(newValue) ? (newValue as number) : newValue?.id,
        manufacturerValidationStatus: ManufacturerValidationStatus.VALID,
      };
      break;
    case 'omit':
      mapData = { ...mapData, omit: +newValue };
      break;
    case 'doNotAssignManufacturer':
      mapData = {
        ...mapData,
        manufacturerValidationStatus: ManufacturerValidationStatus.NO_MANUFACTURER,
      };
      break;
    default:
      break;
  }

  return mapData;
};

export const mapUpsertImportPartsData = (
  data: IImportPart[],
  field: string,
  newValue?: number | { id: number } | boolean,
  isSelectAll = false,
  filters: IImportNotValidPartsFilters = null
): IImportUpsertPartsData => {
  if (isSelectAll) {
    return {
      partImport: [mapUpsertImportPartData(null, field, newValue)],
      filtered: 1,
      filters,
    };
  }

  return {
    partImports: data.map((importPart) => mapUpsertImportPartData(importPart.id, field, newValue)),
  };
};

export const getPossibleManufacturerOptions = (
  manufacturerOptions: IManufacturerOption[]
): IOption[] => {
  let options = [];
  if (manufacturerOptions) {
    options = manufacturerOptions.map((manufacturer) => ({
      id: manufacturer.manufacturerId,
      value: manufacturer.manufacturerId,
      name: manufacturer.manufacturerName,
    }));
  }

  return options;
};

export const getImportHeaderMappingKeys = (type: ImportType) => {
  switch (type) {
    case ImportType.CUSTOMER_QUOTE:
      return [
        ImportHeader.NUMBER,
        ImportHeader.CUSTOMER_PART_NUMBER,
        ImportHeader.MANUFACTURER,
        ImportHeader.DESCRIPTION,
        ImportHeader.QUANTITY,
        ImportHeader.PRICE,
        ImportHeader.COST,
        ImportHeader.COST_SOURCE,
        ImportHeader.VENDOR_QUOTE_NUMBER,
        ImportHeader.REPLACEMENT_COST,
        ImportHeader.NOTES,
        ImportHeader.LEAD_TIME_NOTE,
      ];
    case ImportType.VENDOR_QUOTE:
      return [
        ImportHeader.NUMBER,
        ImportHeader.MANUFACTURER,
        ImportHeader.DESCRIPTION,
        ImportHeader.QUANTITY,
      ];
    case ImportType.JOB:
      return [
        ImportHeader.NUMBER,
        ImportHeader.MANUFACTURER,
        ImportHeader.DESCRIPTION,
        ImportHeader.QUANTITY,
        ImportHeader.NOTES,
      ];
    default:
      return [];
  }
};

export const mapSaveUomsData = (rows: IUomTableRow[], partImport: number): ISaveUomsData => {
  const map: Record<string, string> = {};
  const adjustedQuantity: Record<string, number> = {};
  const omit: Record<string, number> = {};

  rows.forEach((row) => {
    const rowId = String(row.id);

    if (row.assignedUom) {
      map[rowId] = row.assignedUom;
    }

    // Send the row's quantity (pre-filled from the original import, possibly user-edited).
    if (row.adjustedQuantity !== null && row.adjustedQuantity !== undefined) {
      adjustedQuantity[rowId] = row.adjustedQuantity;
    }

    omit[rowId] = +row.omit;
  });

  return { map, adjustedQuantity, omit, partImport };
};

export const getImportPartNotes = (
  part: IImportPart,
  includeQuoteLineMatches = false
): string[] => {
  const notes: string[] = [];

  if (part?.mergeConflicts) {
    const segments: string[] = [];

    if (part?.mergedRowCount >= 2) {
      segments.push(t('noteImportRowsCombined', { rowCount: part.mergedRowCount }));
    }

    if (part.mergeConflicts?.price?.kept) {
      segments.push(
        t('noteImportPricesDiffered', {
          kept: PricingHelper.formatPricing({ amount: Number(part.mergeConflicts.price.kept) }),
        })
      );
    }

    if (part.mergeConflicts?.cost?.kept) {
      segments.push(
        t('noteImportCostsDiffered', {
          kept: PricingHelper.formatPricing({ amount: Number(part.mergeConflicts.cost.kept) }),
        })
      );
    }

    if (segments.length > 0) {
      notes.push(segments.join('; '));
    }
  }

  const quoteLineMatches = part?.quoteLineMatches;

  if (!includeQuoteLineMatches || !quoteLineMatches?.count) {
    return notes;
  }

  const lines = quoteLineMatches.lines ?? [];

  if (1 === quoteLineMatches.count || lines.length < 2) {
    notes.push(t('noteImportQuoteLineMatch', { targetLine: quoteLineMatches.targetLine }));

    return notes;
  }

  notes.push(
    t('noteImportQuoteLineMatches', {
      lines: lines.slice(0, -1).join(', '),
      lastLine: lines[lines.length - 1],
      targetLine: quoteLineMatches.targetLine,
    })
  );

  return notes;
};

export const getImportHeaderLabels = (type: ImportType) => {
  switch (type) {
    case ImportType.CUSTOMER_QUOTE:
      return {
        [ImportHeader.NUMBER]: t('headerPartNumber'),
        [ImportHeader.CUSTOMER_PART_NUMBER]: t('headerCustomerPartNumber'),
        [ImportHeader.MANUFACTURER]: t('headerManufacturer'),
        [ImportHeader.DESCRIPTION]: t('headerDescription'),
        [ImportHeader.QUANTITY]: t('headerQuantity'),
        [ImportHeader.PRICE]: t('headerPrice'),
        [ImportHeader.COST]: t('headerPurchaseCost'),
        [ImportHeader.COST_SOURCE]: t('headerCostNote'),
        [ImportHeader.VENDOR_QUOTE_NUMBER]: t('labelVendorQuoteNumber'),
        [ImportHeader.REPLACEMENT_COST]: t('headerReplacementCost'),
        [ImportHeader.NOTES]: t('headerNotes'),
        [ImportHeader.LEAD_TIME_NOTE]: t('headerLeadTimeNote'),
        [ImportHeader.UOM]: t('headerUom'),
      };
    case ImportType.VENDOR_QUOTE:
      return {
        [ImportHeader.NUMBER]: t('headerPartNumber'),
        [ImportHeader.MANUFACTURER]: t('headerManufacturer'),
        [ImportHeader.DESCRIPTION]: t('headerDescription'),
        [ImportHeader.QUANTITY]: t('headerQuantity'),
        [ImportHeader.UOM]: t('headerUom'),
      };
    case ImportType.JOB:
      return {
        [ImportHeader.NUMBER]: t('headerPartNumber'),
        [ImportHeader.MANUFACTURER]: t('headerManufacturer'),
        [ImportHeader.DESCRIPTION]: t('headerDescription'),
        [ImportHeader.QUANTITY]: t('headerQuantity'),
        [ImportHeader.NOTES]: t('headerNotes'),
        [ImportHeader.UOM]: t('headerUom'),
      };
    default:
      return {};
  }
};
